/**
 * Hooks del dominio del Cotizador de puertas (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). El motor de cálculo (`cotizador_*.py`) es zona prohibida; estos hooks solo
 * orquestan la API REST.
 *
 *  - `useCalcular`: mutación de cálculo (`POST /cotizador/calcular`). No se cachea (depende
 *    del payload del formulario, igual que el SSR pedía por POST en cada COTIZAR).
 *  - `useGuardarCotizacion`: mutación de guardado del historial; invalida la raíz
 *    `['cotizador']` para que el historial refleje el nuevo registro.
 *  - `useTickets`: mutaciones de tickets (producción/cliente), a demanda.
 *  - `useCotizaciones` / `useCotizacionStats`: queries del historial (solo admin), segmentadas
 *    por sede y disparadas con `enabled` (las pestañas de admin las activan al abrirse).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import {
  calcularPuerta,
  fetchCotizaciones,
  fetchCotizacionStats,
  guardarCotizacion,
  ticketCliente,
  ticketProduccion,
  type SaveCotizacionBody,
} from "../api";
import type {
  CalcResult,
  CotizacionHistoryCreated,
  CotizacionHistoryList,
  CotizacionStats,
  CotizadorInput,
  TicketResult,
} from "../types";
import {
  COTIZADOR_ROOT_KEY,
  cotizacionesQueryKey,
  cotizacionStatsQueryKey,
} from "./queryKeys";

/** Margen corto: evita refetch agresivo sin volver el historial rancio. */
const STALE_TIME = 30_000;

/** Cálculo de una cotización (`POST /cotizador/calcular`). Propaga el `ApiError` (400…). */
export function useCalcular() {
  return useMutation<CalcResult, Error, CotizadorInput>({
    mutationFn: (input) => calcularPuerta(input),
  });
}

/**
 * Guardado de una cotización en el historial (`POST /cotizador/cotizaciones`). Tras éxito
 * invalida la raíz de cotizador para que el historial (si está abierto) muestre el registro.
 */
export function useGuardarCotizacion() {
  const queryClient = useQueryClient();
  return useMutation<CotizacionHistoryCreated, Error, SaveCotizacionBody>({
    mutationFn: (body) => guardarCotizacion(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COTIZADOR_ROOT_KEY });
    },
  });
}

/** Ticket de producción a demanda (`POST /cotizador/ticket_produccion`). */
export function useTicketProduccion() {
  return useMutation<TicketResult, Error, CotizadorInput>({
    mutationFn: (input) => ticketProduccion(input),
  });
}

/** Ticket de cliente a demanda (`POST /cotizador/ticket_cliente`). */
export function useTicketCliente() {
  return useMutation<TicketResult, Error, CotizadorInput>({
    mutationFn: (input) => ticketCliente(input),
  });
}

/**
 * Historial de cotizaciones de la sede activa (solo admin). Se pide con `enabled` (la
 * pestaña de historial lo activa al abrirse). `limit` 50 por defecto; `mine=false` trae
 * todas las del admin.
 */
export function useCotizaciones(enabled: boolean, limit = 50, mine = false) {
  const sedeId = getActiveSedeId();
  return useQuery<CotizacionHistoryList>({
    queryKey: cotizacionesQueryKey(sedeId, limit, mine),
    queryFn: () => fetchCotizaciones(limit, mine),
    enabled,
    staleTime: STALE_TIME,
  });
}

/**
 * Estadísticas de error del historial de la sede activa (solo admin). Se pide con `enabled`
 * (la pestaña de estadísticas la activa al abrirse). `days` 30 por defecto.
 */
export function useCotizacionStats(enabled: boolean, days = 30, mine = false) {
  const sedeId = getActiveSedeId();
  return useQuery<CotizacionStats>({
    queryKey: cotizacionStatsQueryKey(sedeId, days, mine),
    queryFn: () => fetchCotizacionStats(days, mine),
    enabled,
    staleTime: STALE_TIME,
  });
}
